/**
 * El dibujo de un producto que todavía no tiene foto.
 *
 * A photo is the product; this is only its kind — a drop for oil, a wheat ear for grain, a
 * pill for medicine — on the tint of its shelf. It is there so a list of products without
 * photos still has a shape down its left edge to skim by, and so a product somebody has not
 * photographed yet looks unfinished rather than broken.
 *
 * Chosen from the name first, in either language, because the name is the most specific
 * thing known; then from the category; then a plain package. Deliberately line icons and not
 * pictures: nothing here should ever be mistaken for a photo of what is on the shelf.
 */

import {
  Apple,
  Baby,
  Banana,
  Bandage,
  Battery,
  Bean,
  Beef,
  Beer,
  BottleWine,
  Boxes,
  BrushCleaning,
  Bubbles,
  Cable,
  Candy,
  Carrot,
  Cherry,
  Citrus,
  Coffee,
  Cookie,
  CookingPot,
  Croissant,
  CupSoda,
  Dessert,
  Droplet,
  Drumstick,
  Egg,
  Fish,
  FlaskConical,
  Flame,
  GlassWater,
  Ham,
  Hammer,
  LeafyGreen,
  Lightbulb,
  Lollipop,
  Milk,
  Nut,
  Package,
  Pill,
  PillBottle,
  Popcorn,
  Sandwich,
  ScrollText,
  ShoppingBag,
  ShoppingBasket,
  ShowerHead,
  Snowflake,
  SoapDispenserDroplet,
  Soup,
  SprayCan,
  Sprout,
  Tablets,
  Toilet,
  Toothbrush,
  Wheat,
} from 'lucide-react';
import { normalizar } from '../lib/texto.js';
import type { Glifo, Tinte } from './Iconos.js';

/**
 * Words in a product's name, Spanish or English, and the drawing they call for. Checked in
 * order, so the more specific word goes first: «leche en polvo» is milk before it is powder,
 * «jabón de lavar» is soap and not laundry in general.
 */
const POR_PALABRA: readonly (readonly [RegExp, Glifo])[] = [
  [/\b(frijol|frijoles|bean|beans)\b/, Bean],
  [/\b(arroz|rice|maiz|corn|maseca|harina|flour|avena|oats|cebada|barley|trigo|cereal)\b/, Wheat],
  [/\b(cafe|coffee|pinolillo|cacao)\b/, Coffee],
  [/\b(leche|milk|yogur|yogurt)\b/, Milk],
  [/\b(huevo|huevos|egg|eggs)\b/, Egg],
  [/\b(pollo|chicken|pechuga)\b/, Drumstick],
  [/\b(jamon|ham|mortadela|mortadella|salchicha|sausage|chorizo)\b/, Ham],
  [/\b(carne|beef|res|cerdo|pork|chuleta)\b/, Beef],
  [/\b(atun|tuna|sardina|sardinas|sardines|pescado|fish)\b/, Fish],
  [/\b(sopa|soup|consome|stock)\b/, Soup],
  [/\b(fideos|noodles|espagueti|spaghetti|pasta)\b(?!\s+dental)/, CookingPot],
  [/\b(gelatina|gelatin|flan)\b/, Dessert],
  [/\b(aceite|oil)\b/, Droplet],
  [/\b(agua|water|suero)\b/, GlassWater],
  [/\b(gaseosa|soda|jugo|juice|refresco|energetica|energy)\b/, CupSoda],
  [/\b(cerveza|beer)\b/, Beer],
  [/\b(ron|rum|vino|wine|guaro)\b/, BottleWine],
  [/\b(hielo|ice)\b/, Snowflake],
  [/\b(platano|plantain|banano|banana|guineo)\b/, Banana],
  [/\b(limon|lime|lemon|naranja|orange)\b/, Citrus],
  [/\b(tomate|tomato)\b/, Cherry],
  [/\b(papa|potato|yuca|cassava|zanahoria|carrot)\b/, Carrot],
  [/\b(cebolla|onion|ajo|garlic)\b/, Sprout],
  [/\b(repollo|cabbage|lechuga|lettuce|chiltoma|pepper)\b/, LeafyGreen],
  [/\b(aguacate|avocado|manzana|apple|mango)\b/, Apple],
  [/\b(pan de molde|sliced bread)\b/, Sandwich],
  [/\b(pan|bread|rolls|pastelito|pastry|rosquillas|tortilla)\b/, Croissant],
  [/\b(galleta|galletas|cookie|cookies)\b/, Cookie],
  [/\b(paleta|lollipop)\b/, Lollipop],
  [/\b(caramelo|caramelos|candy|chicle|chicles|gum|chocolate)\b/, Candy],
  [/\b(churros|meneitos|tostones|chips|boquitas)\b/, Popcorn],
  [/\b(mani|peanuts|nueces|nuts)\b/, Nut],
  [/\b(shampoo|lavaplatos|dish)\b/, SoapDispenserDroplet],
  [/\b(jabon|soap|detergente|detergent|esponja|sponge)\b/, Bubbles],
  [/\b(cloro|bleach|desinfectante|disinfectant|desodorante|deodorant)\b/, SprayCan],
  [/\b(escoba|broom)\b/, BrushCleaning],
  [/\b(papel higienico|toilet)\b/, Toilet],
  [/\b(servilletas|napkins)\b/, ScrollText],
  [/\b(bolsa|bolsas|bags)\b/, ShoppingBag],
  [/\b(fosforos|matches|candela|candle|vela)\b/, Flame],
  [/\b(pasta dental|toothpaste|cepillo|toothbrush)\b/, Toothbrush],
  [/\b(panal|panales|diaper|diapers)\b/, Baby],
  [/\b(curitas|bandages|venda)\b/, Bandage],
  [/\b(jarabe|syrup)\b/, PillBottle],
  [/\b(alka|antiacido|antacid)\b/, Tablets],
  [/\b(alcohol)\b/, FlaskConical],
  [/\b(acetaminofen|acetaminophen|ibuprofeno|ibuprofen|aspirina|aspirin|pastilla|tableta)\b/, Pill],
  [/\b(bombillo|foco|bulb)\b/, Lightbulb],
  [/\b(pila|pilas|batteries|bateria)\b/, Battery],
  [/\b(mecate|rope|cuerda)\b/, Cable],
  [/\b(clavos|nails|martillo)\b/, Hammer],
];

/** The suggested categories, each with a drawing and a tint. Keyed normalised. */
const POR_CATEGORIA: Record<string, readonly [Glifo, Tinte]> = {
  'granos basicos': [Wheat, 'ambar'],
  abarrotes: [ShoppingBasket, 'marca'],
  bebidas: [CupSoda, 'cielo'],
  lacteos: [Milk, 'cielo'],
  'carnes y embutidos': [Beef, 'rosa'],
  'frutas y verduras': [Apple, 'esmeralda'],
  panaderia: [Croissant, 'ambar'],
  golosinas: [Candy, 'violeta'],
  limpieza: [SprayCan, 'cielo'],
  'higiene personal': [ShowerHead, 'violeta'],
  medicamentos: [Pill, 'rosa'],
  ferreteria: [Hammer, 'neutro'],
  mayoreo: [Boxes, 'marca'],
  otros: [Package, 'neutro'],
};

const TINTES_LIBRES: readonly Tinte[] = ['marca', 'cielo', 'violeta', 'ambar', 'rosa', 'esmeralda'];

/**
 * A category somebody typed gets a tint of its own, the same one every time and on every
 * device — derived from its name rather than stored, so there is nothing to sync.
 */
function tinteDe(categoria: string): Tinte {
  let h = 0;
  for (const c of categoria) h = (h * 31 + c.charCodeAt(0)) | 0;
  return TINTES_LIBRES[Math.abs(h) % TINTES_LIBRES.length]!;
}

export interface Pictograma {
  glifo: Glifo;
  tinte: Tinte;
}

/** The drawing a product's name calls for, in either language, if any word in it does. */
export function glifoPorNombre(p: { nombre: string; nombreEn?: string | null }): Glifo | undefined {
  const nombres = normalizar(`${p.nombre} ${p.nombreEn ?? ''}`);
  return POR_PALABRA.find(([patron]) => patron.test(nombres))?.[1];
}

export function pictograma(p: {
  nombre: string;
  nombreEn?: string | null;
  categoria: string;
}): Pictograma {
  const categoria = normalizar(p.categoria);
  const [glifoCategoria, tinte] = POR_CATEGORIA[categoria] ?? [Package, tinteDe(categoria)];
  return { glifo: glifoPorNombre(p) ?? glifoCategoria, tinte };
}

/** For tests: the drawing a category falls back to. */
export const glifoDeCategoria = (categoria: string): Glifo | undefined =>
  POR_CATEGORIA[normalizar(categoria)]?.[0];
